import { useQuery } from '@tanstack/react-query';

import { AudioClip, AudioClipStatus } from '@/api/audioClips/types';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { useViewToken } from '@/context/ViewTokenContext';
import { DateRange } from '@/store';

export type GetAudioClipsResponse = APIResponse<AudioClip[]>;

type GetAudioClipsParams = {
    spotterId: string;
    bristlemouthNodeId: string;
    startMs: number;
    endMs: number;
    status?: AudioClipStatus;
};

/**
 * Returns the clips whose `[start_time, end_time]` overlaps `[startMs, endMs]`, bounds included, sorted by
 * `start_time`. A clip that crosses an edge of the range is included.
 */
const getAudioClips = async (
    { spotterId, bristlemouthNodeId, startMs, endMs, status }: GetAudioClipsParams,
    options?: RequestOptions
): Promise<AudioClip[]> => {
    const response = await axios.get<GetAudioClipsResponse, GetAudioClipsResponse>(
        `/user-rest/devices/${spotterId}/audio`,
        {
            params: {
                bristlemouth_node_id: bristlemouthNodeId,
                start_time: new Date(startMs).toISOString(),
                end_time: new Date(endMs).toISOString(),
                ...(status && { status }),
            },
            ...requestConfig(options),
        }
    );

    return response.data;
};

export type UseAudioClipsOptions = {
    spotterId: string | null | undefined;
    bristlemouthNodeId: string | null | undefined;
    dateRange: DateRange | null | undefined;
    status?: AudioClipStatus;
    config?: QueryConfig<typeof getAudioClips>;
};

export const AUDIO_CLIPS_QUERY_KEY = 'audio-clips';

/**
 * Fetches the audio clips that node `bristlemouthNodeId` of device `spotterId` recorded during `dateRange`.
 *
 * Audio clips are only for the device owner, so the query stays disabled while a view token is present.
 */
export const useAudioClips = ({
    spotterId,
    bristlemouthNodeId,
    dateRange,
    status,
    config: { enabled = true, ...config } = {},
}: UseAudioClipsOptions) => {
    const { loading: isTokenLoading, viewToken } = useViewToken();
    const startMs = dateRange?.startDate.getTime();
    const endMs = dateRange?.endDate.getTime();
    const isEnabled =
        enabled &&
        !!spotterId &&
        !!bristlemouthNodeId &&
        startMs !== undefined &&
        endMs !== undefined &&
        !isTokenLoading &&
        !viewToken;

    return useQuery({
        // milliseconds keep the key stable when the caller builds a new `Date` for the same instant
        queryKey: [AUDIO_CLIPS_QUERY_KEY, { spotterId, bristlemouthNodeId, startMs, endMs, status }],
        queryFn: ({ signal }) =>
            getAudioClips(
                {
                    spotterId: spotterId!,
                    bristlemouthNodeId: bristlemouthNodeId!,
                    startMs: startMs!,
                    endMs: endMs!,
                    status,
                },
                { signal }
            ),
        enabled: isEnabled,
        staleTime: 5000 * 60, // 5 minutes
        ...config,
    });
};
